/**
 * Post a request — five steps (Describe · Scope · Budget & timing · Who can
 * respond · Review). The draft is created on the first save and updated after
 * that; the server says what is still missing (`problems`) before it can be
 * posted. Twasta AI can draft the brief; the person can always type it.
 * Doc: docs/marketplace_get_it_built.md (plan D1, D11).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { fmtDate, getPublicBuildTerms, mktError, money, type BuildExampleRow, type BuildTerms } from '@/api/marketplace'
import {
  createRequest, draftBrief, FEATURE_LABELS, getRequest, KIND_LABELS, postRequest, updateRequest,
  type BriefSection, type BuildRequest, type FeatureKey, type RequestDetails, type RequestInput, type RequestKind,
} from '@/api/marketplaceBuild'
import MarketplaceShell, { Check } from '../MarketplaceShell'
import HostCallout from './HostCallout'
import PaymentStages from './PaymentStages'
import {
  currencySymbol, ErrorPanel, exampleAmount, Loading, minorToMajorText, parseMajorToMinor,
} from './clientShared'

const STEP_LABELS = ['Describe', 'Scope', 'Budget & timing', 'Who can respond', 'Review']

const KIND_HELP: Record<RequestKind, string> = {
  new: 'Built from scratch in a new Twasta project.',
  change: 'New features in an app your organization already uses on Twasta.',
  site: 'Public pages, customer logins, an online shop.',
  analytics: 'Reports and dashboards over a database or data warehouse you already have.',
}
const FEATURE_HELP: Record<FeatureKey, string> = {
  phone: 'People use it on their phone, away from a desk.',
  offline: 'Captures work where there is no signal.',
  approvals: 'Some records need a manager’s sign-off.',
  alerts: 'People are told when something needs them.',
  import: 'Existing records loaded before go-live.',
  dash: 'Management sees costs and trends.',
  portal: 'People outside the company sign in.',
  integrate: 'Data flows to or from another system you already use.',
}
const DIRECTIONS: { id: 'in' | 'out' | 'both'; t: string }[] = [
  { id: 'in', t: 'Into this app' }, { id: 'out', t: 'From this app to it' }, { id: 'both', t: 'Both ways' },
]
const REACH = ['I don’t know — the builder can find out', 'It has an API (REST / SOAP)', 'Direct database access', 'File exports / imports (CSV, Excel)']
const DOCS = ['Not sure', 'Yes — I can share it', 'No']
const DB_KINDS = ['Oracle', 'SQL Server', 'PostgreSQL', 'MySQL', 'Snowflake', 'BigQuery', 'Other']
const DB_SIZES = ['Not sure', 'Under 50 tables', '50 – 500 tables', 'More than 500 tables']
const DB_ACCESS = ['Read-only access we will provide', 'Through the Twasta Database Gateway (on-premise)', 'A copy / export of the data']
const USERS = ['Up to 10', '11 – 100', '101 – 500', 'More than 500']
const DATA = ['None', 'Excel sheets', 'Another system’s database']
const TIMELINES = ['As soon as possible', 'Within 1 month', '1–3 months', 'I am flexible']
const WINDOWS = [3, 7, 14]
const MAX_QUESTIONS = 5
const STARTER_SECTIONS = ['Screens', 'Reports & dashboards', 'Approvals', 'Alerts & connections', 'People & data']

interface Form {
  title: string
  description: string
  kind: RequestKind
  brief: BriefSection[]
  features: FeatureKey[]
  integration: NonNullable<RequestDetails['integration']>
  analytics: NonNullable<RequestDetails['analytics']>
  users: string
  data: string
  budgetMode: 'estimate' | 'range'
  minText: string
  maxText: string
  timeline: string
  startDate: string
  requirements: { certified: boolean; rated: boolean; overlap: boolean }
  questions: string[]
  listingAllowed: boolean
}

const EMPTY: Form = {
  title: '', description: '', kind: 'new', brief: [], features: [],
  integration: { system: '', version: '', direction: 'both', what: '', access: REACH[0], docs: DOCS[0] },
  analytics: { db: '', app: '', size: DB_SIZES[0], access: DB_ACCESS[0] },
  users: '', data: '', budgetMode: 'estimate', minText: '', maxText: '', timeline: '', startDate: '',
  requirements: { certified: false, rated: false, overlap: false }, questions: [], listingAllowed: false,
}

function fromRequest(r: BuildRequest): Form {
  const d = r.details || {}
  return {
    title: r.title === 'Untitled request' ? '' : r.title,
    description: r.description || '',
    kind: r.kind || 'new',
    brief: r.brief || [],
    features: r.features || [],
    integration: { ...EMPTY.integration, ...(d.integration || {}) },
    analytics: { ...EMPTY.analytics, ...(d.analytics || {}) },
    users: d.users || '',
    data: d.data || '',
    budgetMode: r.budget?.mode || 'estimate',
    minText: minorToMajorText(r.budget?.min_minor),
    maxText: minorToMajorText(r.budget?.max_minor),
    timeline: r.timeline || '',
    startDate: r.start_date ? r.start_date.slice(0, 10) : '',
    requirements: { certified: !!r.requirements?.certified, rated: !!r.requirements?.rated, overlap: !!r.requirements?.overlap },
    questions: r.questions || [],
    listingAllowed: !!r.listing_allowed,
  }
}

function toInput(f: Form): RequestInput {
  const details: RequestDetails = {}
  if (f.features.includes('integrate')) details.integration = { ...f.integration, system: f.integration.system.trim() }
  if (f.kind === 'analytics') details.analytics = { ...f.analytics }
  if (f.users) details.users = f.users
  if (f.data) details.data = f.data
  const budget: RequestInput['budget'] = f.budgetMode === 'range'
    ? { mode: 'range', min_minor: parseMajorToMinor(f.minText), max_minor: parseMajorToMinor(f.maxText) }
    : { mode: 'estimate' }
  return {
    title: f.title.trim() || undefined,
    description: f.description,
    kind: f.kind,
    brief: f.brief.map((s) => ({ h: s.h.trim(), items: s.items.map((i) => i.trim()).filter(Boolean) })).filter((s) => s.h && s.items.length),
    features: f.features,
    details,
    budget,
    timeline: f.timeline,
    start_date: f.startDate || null,
    visibility: 'public',
    requirements: f.requirements,
    questions: f.questions.map((q) => q.trim()).filter(Boolean).slice(0, MAX_QUESTIONS),
    listing_allowed: f.listingAllowed,
  }
}

export default function PostRequestPage() {
  const { id: idParam } = useParams()
  const navigate = useNavigate()
  const [id, setId] = useState<number | null>(idParam ? Number(idParam) : null)
  const [form, setForm] = useState<Form>(EMPTY)
  const [req, setReq] = useState<BuildRequest | null>(null)
  const [loading, setLoading] = useState(!!idParam)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [dirty, setDirty] = useState(false)

  const [briefBusy, setBriefBusy] = useState(false)
  const [briefSize, setBriefSize] = useState<string | null>(null)
  const [briefNote, setBriefNote] = useState<string | null>(null)
  const [editBrief, setEditBrief] = useState(false)

  const [windowDays, setWindowDays] = useState(7)
  const [agree, setAgree] = useState(false)
  const [posting, setPosting] = useState(false)
  const [postProblems, setPostProblems] = useState<string[]>([])
  const [posted, setPosted] = useState<BuildRequest | null>(null)

  const [terms, setTerms] = useState<BuildTerms | null>(null)
  const [termsCurrency, setTermsCurrency] = useState('INR')
  const [example, setExample] = useState<BuildExampleRow[]>([])
  const [exampleMinor, setExampleMinor] = useState(0)

  const currency = req?.currency || termsCurrency
  const topRef = useRef<HTMLDivElement | null>(null)

  const load = useCallback(async () => {
    if (!idParam) return
    setLoading(true); setLoadError(null)
    try {
      const r = await getRequest(Number(idParam))
      if (r.status !== 'draft') { navigate(`/marketplace/build/requests/${r.id}`, { replace: true }); return }
      setReq(r); setId(r.id); setForm(fromRequest(r))
    } catch (e) {
      setLoadError(mktError(e, 'This request could not be loaded.').message)
    } finally {
      setLoading(false)
    }
  }, [idParam, navigate])
  useEffect(() => { load() }, [load])

  // The standard terms (for the payment picture and the "after you post" note).
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const base = await getPublicBuildTerms()
        if (!alive) return
        setTerms(base.terms); setTermsCurrency(base.currency)
        const ex = exampleAmount(base.terms, base.currency)
        setExampleMinor(ex)
        if (ex > 0) {
          const withEx = await getPublicBuildTerms(ex)
          if (alive) setExample(withEx.example)
        }
      } catch {
        // The payment picture is an illustration here; the request works without it.
      }
    })()
    return () => { alive = false }
  }, [])

  const set = <K extends keyof Form>(k: K, v: Form[K]) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true) }
  const setInteg = (patch: Partial<Form['integration']>) => { setForm((f) => ({ ...f, integration: { ...f.integration, ...patch } })); setDirty(true) }
  const setAna = (patch: Partial<Form['analytics']>) => { setForm((f) => ({ ...f, analytics: { ...f.analytics, ...patch } })); setDirty(true) }

  /** Create the draft on the first save, update it after. Returns the saved request, or null on error. */
  const save = useCallback(async (quiet = false): Promise<BuildRequest | null> => {
    setSaving(true)
    try {
      const body = toInput(form)
      const r = id ? await updateRequest(id, body) : await createRequest(body)
      setReq(r)
      setPostProblems([])
      if (!id) {
        setId(r.id)
        // Keep the address pointing at the draft (so a reload continues it) without
        // switching routes — a route change would remount the wizard and lose the step.
        try { window.history.replaceState(window.history.state, '', `/marketplace/build/new/${r.id}`) } catch { /* address only */ }
      }
      setSavedAt(new Date()); setDirty(false)
      if (!quiet) toast.success('Draft saved')
      return r
    } catch (e) {
      toast.error(mktError(e, 'The draft could not be saved.').message)
      return null
    } finally {
      setSaving(false)
    }
  }, [form, id])

  const goTo = async (n: number) => {
    if (n === step) return
    // Save on the way so the review step shows the server's own list of what is missing.
    if (dirty || n === 5) {
      const r = await save(true)
      if (!r) return
    }
    setStep(n)
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const makeBrief = async () => {
    if (!form.description.trim()) { toast.error('Describe what the app should do first.'); return }
    setBriefBusy(true); setBriefNote(null)
    try {
      const r = await draftBrief({
        title: form.title, description: form.description, kind: form.kind, features: form.features,
        details: toInput(form).details || {},
      })
      setForm((f) => ({ ...f, brief: r.brief })); setDirty(true)
      setBriefSize(r.size); setEditBrief(false)
    } catch (e) {
      const err = mktError(e, 'The brief could not be drafted.')
      setBriefNote(err.message)
      if (err.code === 'BUILD_AI_UNAVAILABLE') startOwnBrief()
    } finally {
      setBriefBusy(false)
    }
  }

  const startOwnBrief = () => {
    setForm((f) => (f.brief.length ? f : { ...f, brief: STARTER_SECTIONS.map((h) => ({ h, items: [''] })) }))
    setEditBrief(true); setDirty(true)
  }

  // brief editing
  const editSection = (si: number, patch: Partial<BriefSection>) => {
    setForm((f) => ({ ...f, brief: f.brief.map((s, i) => (i === si ? { ...s, ...patch } : s)) })); setDirty(true)
  }
  const editItem = (si: number, ii: number, v: string) => editSection(si, { items: form.brief[si].items.map((x, j) => (j === ii ? v : x)) })
  const addItem = (si: number) => editSection(si, { items: [...form.brief[si].items, ''] })
  const removeItem = (si: number, ii: number) => editSection(si, { items: form.brief[si].items.filter((_, j) => j !== ii) })
  const addSection = () => { setForm((f) => ({ ...f, brief: [...f.brief, { h: '', items: [''] }] })); setDirty(true) }
  const removeSection = (si: number) => { setForm((f) => ({ ...f, brief: f.brief.filter((_, i) => i !== si) })); setDirty(true) }

  const toggleFeature = (k: FeatureKey) => set('features', form.features.includes(k) ? form.features.filter((x) => x !== k) : [...form.features, k])

  const post = async () => {
    const r = await save(true)
    if (!r) return
    if (r.problems?.length) { setPostProblems(r.problems); return }
    setPosting(true); setPostProblems([])
    try {
      const done = await postRequest(r.id, windowDays)
      setPosted(done)
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (e) {
      const err = mktError(e, 'The request could not be posted.')
      if (Array.isArray(err.extra.problems) && err.extra.problems.length) setPostProblems(err.extra.problems)
      toast.error(err.message)
    } finally {
      setPosting(false)
    }
  }

  const restart = () => {
    setPosted(null); setId(null); setReq(null); setForm(EMPTY); setStep(1); setAgree(false)
    setBriefSize(null); setBriefNote(null); setEditBrief(false); setSavedAt(null); setPostProblems([])
    if (idParam) navigate('/marketplace/build/new', { replace: true })
    else { try { window.history.replaceState(window.history.state, '', '/marketplace/build/new') } catch { /* address only */ } }
  }

  // Request strength (a guide for the person, not a rule).
  const filledBrief = form.brief.filter((s) => s.items.some((i) => i.trim()))
  const briefCount = filledBrief.reduce((n, s) => n + s.items.filter((i) => i.trim()).length, 0)
  const integrateOn = form.features.includes('integrate')
  const strength = useMemo(() => {
    let score = 0
    if (form.description.trim().length >= 40) score += 30
    else if (form.description.trim()) score += 15
    if (briefCount > 0) score += 30
    if (form.features.length >= 3) score += 15
    else if (form.features.length) score += 8
    if (form.timeline) score += 10
    if (form.questions.some((q) => q.trim())) score += 5
    if ((!integrateOn || form.integration.system.trim()) && (form.kind !== 'analytics' || form.analytics.db)) score += 10
    return Math.min(100, score)
  }, [form, briefCount, integrateOn])
  const strengthLabel = strength >= 85 ? 'Excellent' : strength >= 60 ? 'Good' : 'Needs detail'
  const strengthTone = strength >= 85 ? 'ok' : strength >= 60 ? 'info' : 'warn'
  const tips: { ok: boolean; t: string }[] = [
    { ok: form.description.trim().length >= 40, t: form.description.trim().length >= 40 ? 'Plain description of what it should do' : 'Describe who uses it and what they do today' },
    { ok: briefCount > 0, t: briefCount > 0 ? 'Brief written — everyone prices the same scope' : 'Draft the brief so estimates are comparable' },
    { ok: form.features.length >= 3, t: `${form.features.length} ${form.features.length === 1 ? 'must-have' : 'must-haves'} ticked` },
    ...(integrateOn ? [{ ok: !!form.integration.system.trim(), t: form.integration.system.trim() ? `Connects to ${form.integration.system.trim()}` : 'Name the system it must connect to' }] : []),
    ...(form.kind === 'analytics' ? [{ ok: !!form.analytics.db, t: form.analytics.db ? `Data is in ${form.analytics.db}` : 'Say where your data is today' }] : []),
  ]

  const featNames = form.features.map((k) => (k === 'integrate' ? `Integrate with ${form.integration.system.trim() || '[system not named yet]'}` : FEATURE_LABELS[k])).join(', ')
  const kindName = form.kind === 'analytics' ? `${KIND_LABELS.analytics}${form.analytics.db ? ` (${form.analytics.db})` : ''}` : KIND_LABELS[form.kind]
  const budgetText = form.budgetMode === 'estimate'
    ? 'Builders estimate'
    : `${money(parseMajorToMinor(form.minText), currency)} – ${money(parseMajorToMinor(form.maxText), currency)}`
  const review: { h: string; v: string; s: number }[] = [
    { h: 'Request', v: `${form.title.trim() || 'No name yet'} — ${form.description.trim() || 'no description yet'}`, s: 1 },
    { h: 'Brief', v: briefCount ? filledBrief.map((s) => `${s.h} (${s.items.filter((i) => i.trim()).length})`).join(' · ') : 'Not written yet — estimates will vary more.', s: 1 },
    { h: 'Kind of work', v: kindName, s: 2 },
    { h: 'Must-haves', v: featNames || 'None selected', s: 2 },
    { h: 'Budget & timing', v: [budgetText, terms ? `Fixed price · ${terms.stages[0]?.pct}% advance, then paid stage by stage` : 'Fixed price, paid stage by stage', form.timeline || 'No timing given', form.startDate ? `start from ${fmtDate(form.startDate)}` : ''].filter(Boolean).join(' · '), s: 3 },
    { h: 'After it’s built', v: `Monthly price quoted by the builder · ${form.listingAllowed ? 'builder may also sell it, without our data' : 'only for us'}`, s: 3 },
    { h: 'Who can respond', v: ['All Twasta builders', form.requirements.certified ? 'Twasta Certified only' : '', form.requirements.rated ? 'well rated only' : '', form.requirements.overlap ? 'works our hours' : '', `open for ${windowDays} days`].filter(Boolean).join(' · '), s: 4 },
  ]
  const problems = postProblems.length ? postProblems : (req?.problems || [])

  if (loading) return <MarketplaceShell><Loading text="Loading your draft…" /></MarketplaceShell>
  if (loadError) return <MarketplaceShell><ErrorPanel message={loadError} onRetry={load} /></MarketplaceShell>

  const choice = (on: boolean) => (on ? 'mkt-choice on' : 'mkt-choice')
  const chip = (on: boolean) => (on ? 'mkt-chip on' : 'mkt-chip')
  const sym = currencySymbol(currency)
  const rangeBad = form.budgetMode === 'range' && (parseMajorToMinor(form.minText) <= 0 || parseMajorToMinor(form.maxText) < parseMajorToMinor(form.minText))

  return (
    <MarketplaceShell>
      <div ref={topRef} className="mkt-wrap" style={{ paddingTop: 28, paddingBottom: 64, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <div>
            <Link to="/marketplace/build" style={{ fontSize: 14, textDecoration: 'none' }}>← Get it built</Link>
            <h1 style={{ fontSize: 30, marginTop: 4 }}>Post a request</h1>
            <p className="mkt-muted" style={{ margin: 0 }}>Five short steps. Posting is free and you are never obliged to order.</p>
          </div>
          {!posted && (
            <span className="mkt-pill" role="status">
              {saving ? 'Saving…' : savedAt ? `Draft saved · ${savedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : id ? 'Draft' : 'Not saved yet'}
            </span>
          )}
        </div>

        {posted ? (
          <div className="mkt-panel" style={{ padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, maxWidth: 760, margin: '0 auto' }}>
            <span aria-hidden="true" style={{ width: 64, height: 64, borderRadius: 999, background: 'var(--color-success-weak)', color: 'var(--color-success)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={34} /></span>
            <h2 style={{ fontSize: 26 }}>Your request is live</h2>
            <p className="mkt-muted" style={{ maxWidth: 520, margin: 0 }}>
              Twasta builders can now see <strong style={{ color: 'var(--color-text)' }}>{posted.title}</strong> and send you estimates
              {posted.closes_at ? <> until <strong style={{ color: 'var(--color-text)' }}>{fmtDate(posted.closes_at)}</strong></> : ''}. Each one arrives on your request page, where you can ask questions, shortlist and compare.
            </p>
            <div className="mkt-row" style={{ justifyContent: 'center' }}>
              <Link className="btn btn-primary" to={`/marketplace/build/requests/${posted.id}`}>See my request</Link>
              <button type="button" className="btn btn-secondary" onClick={restart}>Post another</button>
            </div>
          </div>
        ) : (
          <>
            <ol className="mkt-panel" style={{ listStyle: 'none', margin: 0, padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }} aria-label="Progress">
              {STEP_LABELS.map((label, i) => {
                const n = i + 1
                const done = n < step
                const on = n === step
                return (
                  <li key={label}>
                    <button type="button" onClick={() => goTo(n)} aria-current={on ? 'step' : undefined} disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: on ? 'var(--color-text)' : 'var(--color-text-secondary)', fontWeight: on ? 700 : 500, background: 'transparent', border: 0, font: 'inherit', cursor: 'pointer', padding: '6px 4px', minHeight: 44 }}>
                      <span aria-hidden="true" style={{
                        width: 28, height: 28, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13,
                        border: `1px solid ${done ? 'var(--color-success)' : on ? 'var(--color-accent)' : 'var(--color-border-strong, var(--color-border))'}`,
                        background: done ? 'var(--color-success)' : on ? 'var(--color-accent)' : 'var(--color-bg-secondary)', color: done || on ? '#fff' : 'inherit',
                      }}>{done ? '✓' : n}</span>
                      {label}
                    </button>
                  </li>
                )
              })}
            </ol>

            <div className="mkt-split">
              <div className="mkt-main mkt-panel" style={{ padding: 26 }}>
                {step === 1 && (
                  <div className="mkt-col" style={{ gap: 20 }}>
                    <div><h2 style={{ fontSize: 22 }}>What do you want built?</h2><p className="mkt-muted" style={{ margin: 0 }}>Write it the way you would explain it to a colleague. No technical words needed.</p></div>
                    <div className="mkt-field">
                      <label htmlFor="pr-title">Give it a short name</label>
                      <input id="pr-title" value={form.title} maxLength={120} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Service tracker for our vehicles" />
                    </div>
                    <div className="mkt-field">
                      <label htmlFor="pr-desc">Describe what it should do</label>
                      <textarea id="pr-desc" rows={6} value={form.description} maxLength={6000} onChange={(e) => set('description', e.target.value)}
                        placeholder="Who will use it, what they do today, and what must never go wrong." />
                      <span className="mkt-faint">Tip: mention who will use it, what they do today, and what must never go wrong.</span>
                    </div>

                    {briefNote && (
                      <div className="mkt-banner warn" role="alert"><div><strong>Twasta AI couldn’t draft the brief</strong>{briefNote} You can write the brief yourself below.</div></div>
                    )}

                    {!form.brief.length ? (
                      <div className="mkt-banner info">
                        <div>
                          <strong>Turn this into a brief builders can price</strong>
                          <span className="mkt-muted" style={{ fontSize: 14 }}>Twasta AI lists the screens, reports, approvals and connections your description implies, so every estimate prices the same thing. You can change anything.</span>
                        </div>
                        <div className="mkt-row" style={{ gap: 8 }}>
                          <button type="button" className="btn btn-primary" onClick={makeBrief} disabled={briefBusy}>{briefBusy ? 'Drafting…' : '✦ Draft my brief'}</button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={startOwnBrief}>Write it myself</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ border: '1px solid var(--color-border)', borderRadius: 14, overflow: 'hidden' }}>
                        <div className="mkt-row" style={{ background: 'var(--color-raised, var(--color-bg))', padding: '12px 16px', justifyContent: 'space-between', gap: 8 }}>
                          <strong>Your brief — this is what builders will price</strong>
                          {briefSize && <span className="mkt-pill info">{briefSize}-sized app</span>}
                        </div>
                        {editBrief ? (
                          <div className="mkt-col" style={{ padding: '14px 16px', gap: 16 }}>
                            {form.brief.map((s, si) => (
                              <fieldset key={si} style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 12, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <legend className="mkt-sr">Brief section {si + 1}</legend>
                                <div className="mkt-row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                                  <input aria-label="Section heading" value={s.h} maxLength={60} onChange={(e) => editSection(si, { h: e.target.value })} placeholder="Heading, e.g. Screens"
                                    style={{ flex: '1 1 auto', font: 'inherit', fontWeight: 600, padding: '9px 12px', borderRadius: 10, border: '1px solid var(--color-border-strong, var(--color-border))', background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)' }} />
                                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeSection(si)} aria-label={`Remove section ${s.h || si + 1}`}>Remove section</button>
                                </div>
                                {s.items.map((it, ii) => (
                                  <div key={ii} className="mkt-row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                                    <input aria-label={`${s.h || 'Section'} item ${ii + 1}`} value={it} maxLength={160} onChange={(e) => editItem(si, ii, e.target.value)} placeholder="One thing the app must have"
                                      style={{ flex: '1 1 auto', font: 'inherit', fontSize: 14, padding: '8px 12px', borderRadius: 10, border: '1px solid var(--color-border-strong, var(--color-border))', background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)' }} />
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeItem(si, ii)} aria-label={`Remove item ${it || ii + 1}`}>✕</button>
                                  </div>
                                ))}
                                <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => addItem(si)}>+ Add an item</button>
                              </fieldset>
                            ))}
                            <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={addSection}>+ Add a section</button>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '4px 24px', padding: '14px 16px' }}>
                            {form.brief.map((s, si) => (
                              <div key={si}>
                                <div className="mkt-row" style={{ justifyContent: 'space-between', paddingTop: 8 }}><strong style={{ fontSize: 14 }}>{s.h}</strong><span className="mkt-pill">{s.items.filter((i) => i.trim()).length}</span></div>
                                <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
                                  {s.items.filter((i) => i.trim()).map((it, ii) => <li key={ii} style={{ padding: '6px 0', borderBottom: '1px dashed var(--color-border)', fontSize: 14 }}>{it}</li>)}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mkt-row" style={{ padding: '12px 16px', borderTop: '1px solid var(--color-border)', justifyContent: 'space-between' }}>
                          <span className="mkt-faint">Something missing or wrong? Edit the list — builders see exactly this.</span>
                          <span className="mkt-row" style={{ gap: 8 }}>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditBrief(!editBrief)} aria-pressed={editBrief}>{editBrief ? 'Done editing' : 'Edit brief'}</button>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={makeBrief} disabled={briefBusy}>{briefBusy ? 'Drafting…' : 'Redraft with AI'}</button>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {step === 2 && (
                  <div className="mkt-col" style={{ gap: 20 }}>
                    <div><h2 style={{ fontSize: 22 }}>What kind of work is it?</h2><p className="mkt-muted" style={{ margin: 0 }}>This decides which builders see your request first.</p></div>
                    <div role="group" aria-label="Kind of work" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                      {(Object.keys(KIND_LABELS) as RequestKind[]).map((k) => (
                        <button key={k} type="button" className={choice(form.kind === k)} aria-pressed={form.kind === k} onClick={() => set('kind', k)}>
                          <strong>{KIND_LABELS[k]}</strong><span className="mkt-muted" style={{ fontSize: 14 }}>{KIND_HELP[k]}</span>
                        </button>
                      ))}
                    </div>

                    {form.kind === 'analytics' && (
                      <div className="mkt-col" style={{ border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', background: 'color-mix(in srgb, var(--color-accent) 5%, transparent)', borderRadius: 14, padding: 16, gap: 14 }}>
                        <strong id="pr-db-label">Where is your data today?</strong>
                        <div className="mkt-chips" role="group" aria-labelledby="pr-db-label">
                          {DB_KINDS.map((d) => <button key={d} type="button" className={chip(form.analytics.db === d)} aria-pressed={form.analytics.db === d} onClick={() => setAna({ db: d })}>{d}</button>)}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
                          <div className="mkt-field"><label htmlFor="pr-dbapp">Which application fills it?</label><input id="pr-dbapp" value={form.analytics.app || ''} maxLength={120} onChange={(e) => setAna({ app: e.target.value })} placeholder="e.g. our ERP, our own billing system" /></div>
                          <div className="mkt-field"><label htmlFor="pr-dbsize">Roughly how big?</label>
                            <select id="pr-dbsize" value={form.analytics.size || DB_SIZES[0]} onChange={(e) => setAna({ size: e.target.value })}>{DB_SIZES.map((o) => <option key={o}>{o}</option>)}</select></div>
                          <div className="mkt-field"><label htmlFor="pr-dbconn">How will the builder reach it?</label>
                            <select id="pr-dbconn" value={form.analytics.access || DB_ACCESS[0]} onChange={(e) => setAna({ access: e.target.value })}>{DB_ACCESS.map((o) => <option key={o}>{o}</option>)}</select></div>
                        </div>
                        <span className="mkt-faint">Builders never see your data until you order and give access. Read-only access is enough for analytics.</span>
                        {!form.analytics.db && <span className="mkt-faint" style={{ color: 'var(--color-warning)' }}>Choose where your data is so builders can price the work.</span>}
                      </div>
                    )}

                    <div><span style={{ fontWeight: 600, fontSize: 14 }}>Must-haves</span><p className="mkt-faint" style={{ margin: 0 }}>Tick everything that is essential. Builders must say how they will handle each one.</p></div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                      {(Object.keys(FEATURE_LABELS) as FeatureKey[]).map((k) => (
                        <label key={k} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 12, cursor: 'pointer', fontWeight: 500, fontSize: 15, minHeight: 44 }}>
                          <input type="checkbox" checked={form.features.includes(k)} onChange={() => toggleFeature(k)} style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--color-accent)' }} />
                          <span>{FEATURE_LABELS[k]}<span className="mkt-faint" style={{ display: 'block', fontWeight: 400 }}>{FEATURE_HELP[k]}</span></span>
                        </label>
                      ))}
                    </div>

                    {integrateOn && (
                      <div className="mkt-col" style={{ border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', background: 'color-mix(in srgb, var(--color-accent) 5%, transparent)', borderRadius: 14, padding: 16, gap: 14 }}>
                        <strong>Which system should it connect to?</strong>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
                          <div className={`mkt-field${form.integration.system.trim() ? '' : ' warn'}`}>
                            <label htmlFor="pr-sys">System name</label>
                            <input id="pr-sys" value={form.integration.system} maxLength={120} onChange={(e) => setInteg({ system: e.target.value })} placeholder="The product’s name, or what it does if it was built for you" />
                            <span className="mkt-faint">Name the product you use, or describe it if it was built for you.</span>
                          </div>
                          <div className="mkt-field">
                            <label htmlFor="pr-sysv">Version or edition <span className="mkt-faint" style={{ fontWeight: 400 }}>(if you know it)</span></label>
                            <input id="pr-sysv" value={form.integration.version || ''} maxLength={60} onChange={(e) => setInteg({ version: e.target.value })} />
                          </div>
                        </div>
                        <div className="mkt-field">
                          <span id="pr-dir" style={{ fontWeight: 600, fontSize: 14 }}>Which way does the data move?</span>
                          <div className="mkt-chips" role="group" aria-labelledby="pr-dir">
                            {DIRECTIONS.map((d) => <button key={d.id} type="button" className={chip(form.integration.direction === d.id)} aria-pressed={form.integration.direction === d.id} onClick={() => setInteg({ direction: d.id })}>{d.t}</button>)}
                          </div>
                        </div>
                        <div className="mkt-field">
                          <label htmlFor="pr-sysw">What should move between them?</label>
                          <input id="pr-sysw" value={form.integration.what || ''} maxLength={400} onChange={(e) => setInteg({ what: e.target.value })} placeholder="e.g. orders go to the other system; the customer list comes back daily" />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
                          <div className="mkt-field"><label htmlFor="pr-sysa">How can it be reached?</label>
                            <select id="pr-sysa" value={form.integration.access || REACH[0]} onChange={(e) => setInteg({ access: e.target.value })}>{REACH.map((o) => <option key={o}>{o}</option>)}</select></div>
                          <div className="mkt-field"><label htmlFor="pr-sysd">Is there documentation?</label>
                            <select id="pr-sysd" value={form.integration.docs || DOCS[0]} onChange={(e) => setInteg({ docs: e.target.value })}>{DOCS.map((o) => <option key={o}>{o}</option>)}</select></div>
                        </div>
                        {!form.integration.system.trim() && <span className="mkt-faint" style={{ color: 'var(--color-warning)' }}>Add the system’s name so builders can price the connection.</span>}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                      <div className="mkt-field"><label htmlFor="pr-users">About how many people will use it?</label>
                        <select id="pr-users" value={form.users} onChange={(e) => set('users', e.target.value)}><option value="">Not sure</option>{USERS.map((o) => <option key={o}>{o}</option>)}</select></div>
                      <div className="mkt-field"><label htmlFor="pr-data">Existing data to bring in</label>
                        <select id="pr-data" value={form.data} onChange={(e) => set('data', e.target.value)}><option value="">Not sure</option>{DATA.map((o) => <option key={o}>{o}</option>)}</select></div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="mkt-col" style={{ gap: 20 }}>
                    <div><h2 style={{ fontSize: 22 }}>Budget and timing</h2><p className="mkt-muted" style={{ margin: 0 }}>Not sure? Let builders tell you. You can still decide later.</p></div>
                    <div role="group" aria-label="Budget" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                      <button type="button" className={choice(form.budgetMode === 'estimate')} aria-pressed={form.budgetMode === 'estimate'} onClick={() => set('budgetMode', 'estimate')}>
                        <strong>Ask builders to estimate <span className="mkt-pill ok" style={{ marginLeft: 6 }}>Recommended</span></strong>
                        <span className="mkt-muted" style={{ fontSize: 14 }}>Each builder prices your brief. Good when you are unsure.</span>
                      </button>
                      <button type="button" className={choice(form.budgetMode === 'range')} aria-pressed={form.budgetMode === 'range'} onClick={() => set('budgetMode', 'range')}>
                        <strong>I have a budget range</strong>
                        <span className="mkt-muted" style={{ fontSize: 14 }}>Builders see it and propose within it.</span>
                      </button>
                    </div>
                    {form.budgetMode === 'range' && (
                      <div className="mkt-row" style={{ alignItems: 'flex-end', gap: 14 }}>
                        <div className="mkt-field"><label htmlFor="pr-bmin">From</label>
                          <div className={`mkt-money${rangeBad ? ' warn' : ''}`}><span>{sym}</span><input id="pr-bmin" inputMode="decimal" value={form.minText} onChange={(e) => set('minText', e.target.value.replace(/[^\d.,]/g, ''))} /></div></div>
                        <div className="mkt-field"><label htmlFor="pr-bmax">Up to</label>
                          <div className={`mkt-money${rangeBad ? ' warn' : ''}`}><span>{sym}</span><input id="pr-bmax" inputMode="decimal" value={form.maxText} onChange={(e) => set('maxText', e.target.value.replace(/[^\d.,]/g, ''))} /></div></div>
                        {rangeBad && <span className="mkt-faint" style={{ color: 'var(--color-warning)', paddingBottom: 12 }}>Enter a “from” above zero and an “up to” at least as high.</span>}
                      </div>
                    )}

                    <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>How you pay — Twasta’s standard terms</span>
                        <p className="mkt-faint" style={{ margin: 0 }}>A fixed price, paid in stages{exampleMinor > 0 ? `. Example on a ${money(exampleMinor, termsCurrency)} estimate:` : '.'}</p>
                      </div>
                      {terms
                        ? <PaymentStages stages={terms.stages} rows={example.length ? example : undefined} amountMinor={exampleMinor} currency={termsCurrency} who="client" advanceRelease={terms.advance_release} />
                        : <span className="mkt-faint" role="status">Loading the terms…</span>}
                    </div>

                    <div className="mkt-field">
                      <span id="pr-when" style={{ fontWeight: 600, fontSize: 14 }}>When do you need it live?</span>
                      <div className="mkt-chips" role="group" aria-labelledby="pr-when">
                        {TIMELINES.map((t) => <button key={t} type="button" className={chip(form.timeline === t)} aria-pressed={form.timeline === t} onClick={() => set('timeline', form.timeline === t ? '' : t)}>{t}</button>)}
                      </div>
                    </div>
                    <div className="mkt-field" style={{ maxWidth: 280 }}>
                      <label htmlFor="pr-start">Earliest start</label>
                      <input id="pr-start" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
                    </div>

                    <div className="mkt-col" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 18 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>After it’s built</span>
                        <p className="mkt-faint" style={{ margin: 0 }}>Builders also quote a monthly price to use the app — hosting, updates and fixes included. It is paid from your wallet, like any Marketplace app.</p>
                      </div>
                      <HostCallout currency={currency} />
                      <span style={{ fontWeight: 600, fontSize: 14 }}>May the builder also sell this app to other companies?</span>
                      <div role="group" aria-label="May the builder also sell this app" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                        <button type="button" className={choice(!form.listingAllowed)} aria-pressed={!form.listingAllowed} onClick={() => set('listingAllowed', false)}>
                          <strong>No — only for us</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Nobody else can use this app.</span>
                        </button>
                        <button type="button" className={choice(form.listingAllowed)} aria-pressed={form.listingAllowed} onClick={() => set('listingAllowed', true)}>
                          <strong>Yes, without our data</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Builders usually charge less to build. Your data and settings never go with it.</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="mkt-col" style={{ gap: 20 }}>
                    <div><h2 style={{ fontSize: 22 }}>Who can send you an estimate?</h2><p className="mkt-muted" style={{ margin: 0 }}>Your request is shown to Twasta builders who match it.</p></div>
                    <div className="mkt-choice on" style={{ cursor: 'default' }}>
                      <strong>All Twasta builders</strong>
                      <span className="mkt-muted" style={{ fontSize: 14 }}>Anyone who matches can send an estimate. Most choice. Your company name is shown only to builders you message or order from.</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                      {([
                        ['certified', 'Twasta Certified only', 'Builders who passed the Twasta skills check.'],
                        ['rated', 'Well rated only', 'Builders with good ratings from finished orders.'],
                        ['overlap', 'Works during our hours', 'Available for calls during your working day.'],
                      ] as const).map(([k, t, d]) => (
                        <label key={k} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 12, cursor: 'pointer', fontWeight: 500, fontSize: 15, minHeight: 44 }}>
                          <input type="checkbox" checked={form.requirements[k]} onChange={() => set('requirements', { ...form.requirements, [k]: !form.requirements[k] })} style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--color-accent)' }} />
                          <span>{t}<span className="mkt-faint" style={{ display: 'block', fontWeight: 400 }}>{d}</span></span>
                        </label>
                      ))}
                    </div>
                    <div className="mkt-field">
                      <span style={{ fontWeight: 600, fontSize: 14 }}>Questions every builder must answer <span className="mkt-faint" style={{ fontWeight: 400 }}>(up to {MAX_QUESTIONS})</span></span>
                      {form.questions.length === 0 && <span className="mkt-faint">None yet. Ask what matters to you, for example whether they have built something similar.</span>}
                      {form.questions.map((q, i) => (
                        <div key={i} className="mkt-row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                          <input aria-label={`Question ${i + 1}`} value={q} maxLength={300} onChange={(e) => set('questions', form.questions.map((x, j) => (j === i ? e.target.value : x)))} style={{ flex: '1 1 auto' }} />
                          <button type="button" className="btn btn-secondary btn-sm" aria-label={`Remove question ${i + 1}`} onClick={() => set('questions', form.questions.filter((_, j) => j !== i))}>✕</button>
                        </div>
                      ))}
                      <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} disabled={form.questions.length >= MAX_QUESTIONS} onClick={() => set('questions', [...form.questions, ''])}>+ Add a question</button>
                    </div>
                    <div className="mkt-field">
                      <span id="pr-win" style={{ fontWeight: 600, fontSize: 14 }}>Accept estimates for</span>
                      <div className="mkt-chips" role="group" aria-labelledby="pr-win">
                        {WINDOWS.map((w) => <button key={w} type="button" className={chip(windowDays === w)} aria-pressed={windowDays === w} onClick={() => setWindowDays(w)}>{w} days</button>)}
                      </div>
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div className="mkt-col" style={{ gap: 16 }}>
                    <div><h2 style={{ fontSize: 22 }}>Check and post</h2><p className="mkt-muted" style={{ margin: 0 }}>This is what builders will see. Your company name is shown only to builders you message or order from.</p></div>
                    {problems.length > 0 && (
                      <div className="mkt-banner warn" role="alert">
                        <div>
                          <strong>Before you can post</strong>
                          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
                        </div>
                      </div>
                    )}
                    {review.map((r) => (
                      <div key={r.h} className="mkt-row" style={{ border: '1px solid var(--color-border)', borderRadius: 14, padding: '14px 16px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: '1 1 400px' }}><div className="mkt-faint" style={{ fontWeight: 600 }}>{r.h}</div><div style={{ fontSize: 15 }}>{r.v}</div></div>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => goTo(r.s)} aria-label={`Edit ${r.h}`}>Edit</button>
                      </div>
                    ))}
                    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 12, cursor: 'pointer', fontSize: 15 }}>
                      <input type="checkbox" checked={agree} onChange={() => setAgree(!agree)} style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--color-accent)' }} />
                      <span>I understand the builder builds the app in their own Twasta account, and it is installed in my organization when I accept it. We pay Twasta, and Twasta pays the builder after we approve each stage.</span>
                    </label>
                  </div>
                )}

                <div className="mkt-row" style={{ justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 18 }}>
                  <button type="button" className="btn btn-secondary" onClick={() => goTo(step - 1)} disabled={step === 1 || saving}>Back</button>
                  <div className="mkt-row" style={{ gap: 10 }}>
                    <button type="button" className="btn btn-ghost" onClick={() => save(false)} disabled={saving}>{saving ? 'Saving…' : 'Save draft'}</button>
                    {step < 5
                      ? <button type="button" className="btn btn-primary" onClick={() => goTo(step + 1)} disabled={saving}>Continue</button>
                      : <button type="button" className="btn btn-primary" onClick={post} disabled={!agree || posting || saving || problems.length > 0}>{posting ? 'Posting…' : 'Post request — it’s free'}</button>}
                  </div>
                </div>
              </div>

              <aside className="mkt-aside" style={{ maxWidth: 380 }}>
                <div className="mkt-panel" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div className="mkt-row" style={{ justifyContent: 'space-between' }}><strong>Request strength</strong><span className={`mkt-pill ${strengthTone}`}>{strengthLabel}</span></div>
                  <div className="mkt-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={strength} aria-label="Request strength">
                    <div className={strength >= 60 ? '' : 'warn'} style={{ width: `${strength}%`, background: strength >= 85 ? 'var(--color-success)' : undefined }} />
                  </div>
                  <p className="mkt-muted" style={{ fontSize: 14, margin: 0 }}>Clear requests get more estimates, and the prices are closer together.</p>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {tips.map((t) => (
                      <li key={t.t} style={{ display: 'flex', gap: 8, fontSize: 14, alignItems: 'flex-start' }}>
                        <span aria-hidden="true" style={{ width: 20, height: 20, borderRadius: 999, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: t.ok ? 'var(--color-success-weak)' : 'var(--color-warning-weak)', color: t.ok ? 'var(--color-success)' : 'var(--color-warning)' }}>{t.ok ? '✓' : '!'}</span>
                        <span><span className="mkt-sr">{t.ok ? 'Done: ' : 'To do: '}</span>{t.t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mkt-panel" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <strong>What happens after you post</strong>
                  <ol className="mkt-muted" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
                    <li>Builders who match can see it and send estimates.</li>
                    <li>Estimates arrive on your request page.</li>
                    <li>Ask questions, shortlist and compare — free.</li>
                    <li>Nothing to pay until you place an order{terms?.stages[0] ? <> — then a {terms.stages[0].pct}% advance to Twasta</> : ''}.</li>
                    <li>When you accept the work, it is installed in your organization and appears in <strong style={{ color: 'var(--color-text)' }}>My apps</strong>.</li>
                  </ol>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>
    </MarketplaceShell>
  )
}
